// SEO per dienst: Opportunity Scan, Slim rapport, Lead- en salesmodule, Growth Dashboard.
// Elke dienst in één oogopslag: bezoek, AI-zichtbaarheid, leads en de technische staat van de eigen pagina's.
// Alleen echte bronnen: ontbreekt een bron, dan staat er "Geen gegevens beschikbaar", nooit een schatting.
// Ontwerp: docs/Ontwerp_SEO_Per_Dienst.md
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";
import { C, Kop, Tellers, Groep, Rij, Leeg, Stat, Chip, Melding } from "./gedeeld";

const GEEN = "Geen gegevens beschikbaar";

const padVan = (url) => { try { return new URL(url).pathname.replace(/(.)\/$/, "$1") || "/"; } catch (e) { return url || ""; } };
const hoortBij = (pad, paden) => paden.some(p => pad === p || (p !== "/" && pad.startsWith(p + "/")));
const procent = (n, t) => (t ? Math.round((n / t) * 100) + "%" : "-");

export default function SeoPerDienst() {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  const [toast, setToast] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [nieuwPad, setNieuwPad] = useState({});
  const [nieuweVraag, setNieuweVraag] = useState({});

  const melding = (m) => { setToast(m); setTimeout(() => setToast(null), 3500); };

  const laad = useCallback(async () => {
    try {
      const { data: site, error: se } = await supabase.from("seo_sites").select("id,naam,domein").eq("domein", "dif3r3nt.nl").maybeSingle();
      if (se) throw new Error(se.message);
      if (!site) { setData({ site: null }); return; }
      const [dn, pg, pr, rn, is, au, ci] = await Promise.all([
        supabase.from("seo_diensten").select("*").eq("site_id", site.id).eq("actief", true).order("volgorde"),
        supabase.from("seo_dienst_paginas").select("*"),
        supabase.from("geo_prompts").select("*").eq("site_id", site.id),
        supabase.from("geo_runs").select("*").eq("site_id", site.id).order("created_at", { ascending: false }).limit(200),
        supabase.from("seo_issues").select("url,code,ernst,status").eq("site_id", site.id).eq("status", "open").limit(1500),
        supabase.from("seo_audits").select("score,klaar_op,status").eq("site_id", site.id).eq("status", "klaar").order("gestart_op", { ascending: false }).limit(1),
        supabase.rpc("seo_dienst_cijfers"),
      ]);
      const eerste = [dn, pg, pr, rn, is].find(x => x.error);
      if (eerste) throw new Error(eerste.error.message);
      setData({
        site, diensten: dn.data || [], paginas: pg.data || [], prompts: pr.data || [], runs: rn.data || [],
        issues: is.data || [], audit: (au.data || [])[0] || null,
        cijfers: ci.error ? {} : Object.fromEntries((ci.data || []).map(c => [c.dienst_id, c])),
        cijferFout: ci.error ? ci.error.message : null,
      });
      setFout(null);
    } catch (ex) { setFout(ex.message || "Kon de gegevens niet laden"); }
  }, []);

  useEffect(() => { laad(); }, [laad]);

  async function synchroniseer() {
    setBezig(true);
    try {
      const { data: d, error } = await supabase.functions.invoke("seo-zoekdata", { body: { action: "sync" } });
      let msg = error && (error.message || "Aanroep mislukt");
      if (error) { try { const j = await error.context.json(); if (j && j.error) msg = j.error; } catch (e) { /* standaardbericht */ } }
      if (msg) throw new Error(msg);
      if (d && d.error) throw new Error(d.error);
      const eerste = d && d.gesynchroniseerd ? Object.values(d.gesynchroniseerd)[0] : null;
      melding("✓ Zoekdata opgehaald" + (eerste ? " (" + eerste.rijen + " regels)" : ""));
      await laad();
    } catch (ex) { melding("✗ " + (ex.message || "Mislukt")); }
    setBezig(false);
  }

  async function doe(fn, ok) {
    try { await fn(); if (ok) melding("✓ " + ok); await laad(); }
    catch (ex) { melding("✗ " + (ex.message || "Mislukt")); }
  }

  const rijen = useMemo(() => {
    if (!data || !data.site) return [];
    return data.diensten.map(d => {
      const paden = data.paginas.filter(p => p.dienst_id === d.id).map(p => p.pad);
      const prompts = data.prompts.filter(p => p.dienst_id === d.id);
      const laatste = (pid) => data.runs.find(r => r.prompt_id === pid);
      const gemeten = prompts.filter(p => p.actief).map(p => laatste(p.id)).filter(r => r && !r.fout && !r.waarschuwing);
      const issues = data.issues.filter(i => hoortBij(padVan(i.url), paden));
      return {
        d, paden, prompts, laatste,
        geo: { totaal: gemeten.length, geciteerd: gemeten.filter(r => r.geciteerd).length, genoemd: gemeten.filter(r => r.genoemd || r.geciteerd).length },
        issues, hoog: issues.filter(i => i.ernst === "hoog").length,
        c: data.cijfers[d.id] || {},
      };
    });
  }, [data]);

  if (fout) return <Melding type="fout">{fout}</Melding>;
  if (!data) return <Leeg tekst="Laden..." />;
  if (!data.site) return <Leeg tekst="De site dif3r3nt.nl staat niet in het sitesregister." />;

  const totPag = rijen.reduce((t, r) => t + r.paden.length, 0);
  const geoT = rijen.reduce((t, r) => t + r.geo.totaal, 0);
  const geoG = rijen.reduce((t, r) => t + r.geo.geciteerd, 0);
  const hoogT = rijen.reduce((t, r) => t + r.hoog, 0);

  // Volgende stap, afgeleid van wat er echt is (geen verzonnen advies).
  function volgendeStap(r) {
    if (!r.paden.length) return "Koppel de pagina's van deze dienst. Zonder pagina's valt er niets te meten.";
    if (!r.prompts.length) return "Voeg klantvragen toe zodat de AI-zichtbaarheid van deze dienst gemeten wordt.";
    if (r.geo.totaal === 0) return "De klantvragen zijn nog niet gemeten; de planning pakt ze vanzelf op.";
    if (r.hoog > 0) return "Los eerst de " + r.hoog + " hoge technische problemen op deze pagina's op.";
    if (r.c.gsc_kansen && r.c.gsc_kansen.length) return "Verbeter de pagina voor \"" + r.c.gsc_kansen[0].zoekwoord + "\": die staat op positie " + String(r.c.gsc_kansen[0].positie).replace(".", ",") + " en zit dicht bij de top.";
    if (r.geo.geciteerd === 0) return "De dienst wordt nog niet geciteerd in AI-antwoorden: schrijf een antwoord-eerst pagina op een van de niet-gevonden vragen.";
    return "Geen dringende actie.";
  }

  const knop = { fontSize: 11 };
  const veld = { background: C.bg, border: "1px solid " + C.border, color: C.text, borderRadius: 6, padding: "6px 8px", fontSize: 12, minWidth: 0, flex: 1 };

  return (
    <div>
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, color: toast.startsWith("✗") ? C.red : C.green, padding: "9px 18px", borderRadius: 8, fontSize: 13, zIndex: 999, maxWidth: "92vw" }}>{toast}</div>}
      <Kop titel="SEO per dienst" sub="Per dienst van dif3r3nt.nl: bezoek, AI-zichtbaarheid, leads en technische staat. Alles uit echte bronnen."
        rechts={<button className="btn bg" style={knop} disabled={bezig} onClick={synchroniseer}>{bezig ? "Bezig..." : "Haal zoekdata op"}</button>} />
      <Tellers items={[
        { label: "Diensten", waarde: rijen.length },
        { label: "Pagina's gekoppeld", waarde: totPag },
        { label: "AI-geciteerd", waarde: procent(geoG, geoT), kleur: geoT && geoG ? C.green : C.muted },
        { label: "Hoge problemen", waarde: hoogT, kleur: hoogT ? C.red : C.green },
      ]} />
      {data.cijferFout && <Melding type="fout">Bezoekcijfers niet beschikbaar: {data.cijferFout}</Melding>}

      <Groep label="Diensten" kleur={C.green} aantal={rijen.length}>
        {rijen.map(r => {
          const bezoek = r.c.bezoekers_30d, vorig = r.c.bezoekers_vorige_30d;
          const trend = bezoek != null && vorig ? Math.round(((bezoek - vorig) / vorig) * 100) : null;
          return (
            <Rij
              key={r.d.id}
              titel={r.d.naam}
              kleur={!r.paden.length ? C.dim : r.hoog ? C.red : r.geo.geciteerd ? C.green : C.yellow}
              meta={<>
                <span>{r.paden.length} pagina{r.paden.length === 1 ? "" : "'s"}</span>
                <span>{bezoek != null ? bezoek + " bezoekers (30 d)" : "bezoek: " + GEEN.toLowerCase()}</span>
                <span>{r.geo.totaal ? "AI: " + r.geo.geciteerd + "/" + r.geo.totaal + " geciteerd" : "AI: nog niet gemeten"}</span>
              </>}
            >
              {r.d.omschrijving && <p style={{ fontSize: 12, color: C.muted, margin: "0 0 10px" }}>{r.d.omschrijving}</p>}

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 8, marginBottom: 12 }}>
                <Stat label="Bezoekers (30 d)" waarde={bezoek != null ? bezoek : "-"} sub={bezoek == null ? GEEN : trend != null ? (trend >= 0 ? "+" : "") + trend + "% t.o.v. vorige 30 d" : "geen vergelijking"} />
                <Stat label="Via zoekmachine" waarde={r.c.organisch_30d != null ? r.c.organisch_30d : "-"} sub={r.c.organisch_30d == null ? GEEN : "uit de bezoekstatistiek"} />
                <Stat label="Leads (30 d)" waarde={r.c.leads_30d != null ? r.c.leads_30d : "-"} sub={r.c.leads_30d == null ? "Volgt zodra de website de dienst meegeeft" : r.c.leads_totaal + " totaal, " + r.c.leads_gewonnen + " gewonnen. Aanvragen met rapport (Opportunity Scan en Slim rapport tellen dezelfde)"} />
                <Stat label="AI geciteerd" waarde={procent(r.geo.geciteerd, r.geo.totaal)} sub={r.geo.totaal ? r.geo.genoemd + " genoemd van " + r.geo.totaal : "nog niet gemeten"} kleur={r.geo.geciteerd ? C.green : undefined} />
                <Stat label="Open problemen" waarde={r.paden.length ? r.issues.length : "-"} sub={r.paden.length ? r.hoog + " hoog" : "Koppel pagina's"} kleur={r.hoog ? C.red : undefined} />
                <Stat label="Zoekklikken (28 d)" waarde={r.c.gsc_klikken != null ? r.c.gsc_klikken : "-"} sub={r.c.gsc_klikken == null ? GEEN + " (Search Console)" : r.c.gsc_vertoningen + " vertoningen"} />
                <Stat label="Zoekwoorden in top 10" waarde={r.c.gsc_top10 != null ? r.c.gsc_top10 : "-"} sub={r.c.gsc_top10 == null ? GEEN + " (Search Console)" : "van " + r.c.gsc_zoekwoorden + " zoekwoorden"} kleur={r.c.gsc_top10 ? C.green : undefined} />
                <Stat label="Backlinks" waarde="-" sub={GEEN + " (nog niet gekoppeld)"} />
              </div>

              {(r.c.gsc_kansen || []).length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <p className="lbl" style={{ margin: "0 0 6px" }}>Kansen: positie 4 tot 15</p>
                  {r.c.gsc_kansen.map(k => (
                    <div key={k.zoekwoord} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "5px 0", borderTop: "1px solid " + C.border, fontSize: 12 }}>
                      <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{k.zoekwoord}</span>
                      <span style={{ color: C.muted, whiteSpace: "nowrap" }}>positie {String(k.positie).replace(".", ",")} · {k.vertoningen} vertoningen</span>
                    </div>
                  ))}
                  <p style={{ fontSize: 11, color: C.dim, margin: "4px 0 0" }}>Pagina's die hier al in beeld komen maar net niet bovenaan staan. Daar levert verbeteren het snelst winst op.</p>
                </div>
              )}

              <div style={{ background: C.surfaceHigh, border: "1px solid " + C.border, borderRadius: 8, padding: "8px 10px", marginBottom: 12 }}>
                <p className="lbl" style={{ margin: 0, fontSize: 9 }}>Volgende stap</p>
                <p style={{ fontSize: 12, margin: "3px 0 0" }}>{volgendeStap(r)}</p>
              </div>

              <p className="lbl" style={{ margin: "0 0 6px" }}>Pagina's van deze dienst</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                {r.paden.map(p => (
                  <span key={p} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, background: C.surface, border: "1px solid " + C.border, borderRadius: 6, padding: "3px 8px" }}>
                    {p}
                    <span style={{ cursor: "pointer", color: C.dim }} title="Ontkoppel" onClick={() => doe(async () => { const { error } = await supabase.from("seo_dienst_paginas").delete().eq("dienst_id", r.d.id).eq("pad", p); if (error) throw new Error(error.message); }, "Pagina ontkoppeld")}>✕</span>
                  </span>
                ))}
                {!r.paden.length && <span style={{ fontSize: 11, color: C.dim }}>Nog geen pagina's gekoppeld.</span>}
              </div>
              <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                <input style={veld} placeholder="/pad-van-de-pagina" value={nieuwPad[r.d.id] || ""} onChange={e => setNieuwPad({ ...nieuwPad, [r.d.id]: e.target.value })} />
                <button className="btn bg" style={knop} onClick={() => {
                  let p = String(nieuwPad[r.d.id] || "").trim().replace(/^https?:\/\/[^/]+/, "");
                  if (!p) return;
                  if (!p.startsWith("/")) p = "/" + p;
                  p = p.replace(/(.)\/$/, "$1");
                  doe(async () => { const { error } = await supabase.from("seo_dienst_paginas").upsert([{ dienst_id: r.d.id, pad: p }]); if (error) throw new Error(error.message); setNieuwPad({ ...nieuwPad, [r.d.id]: "" }); }, "Pagina gekoppeld");
                }}>Koppel</button>
              </div>

              <p className="lbl" style={{ margin: "0 0 6px" }}>Klantvragen (AI-zichtbaarheid)</p>
              {r.prompts.map(p => {
                const run = r.laatste(p.id);
                const kleur = !run ? C.dim : run.fout ? C.red : run.geciteerd ? C.green : run.genoemd || run.in_bronnen ? C.blue : C.red;
                const tekst = !run ? "nog niet gemeten" : run.fout ? "fout" : run.waarschuwing ? "onbetrouwbaar" : run.geciteerd ? "geciteerd" : run.genoemd ? "genoemd" : run.in_bronnen ? "in bronnen" : "niet gevonden";
                return (
                  <div key={p.id} style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between", padding: "5px 0", borderTop: "1px solid " + C.border, fontSize: 12 }}>
                    <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{p.vraag}</span>
                    <Chip tekst={tekst} kleur={kleur} />
                  </div>
                );
              })}
              <div style={{ display: "flex", gap: 6, margin: "8px 0 12px" }}>
                <input style={veld} placeholder="Een vraag zoals een klant die stelt" value={nieuweVraag[r.d.id] || ""} onChange={e => setNieuweVraag({ ...nieuweVraag, [r.d.id]: e.target.value })} />
                <button className="btn bg" style={knop} onClick={() => {
                  const v = String(nieuweVraag[r.d.id] || "").trim();
                  if (v.length < 8) { melding("✗ Schrijf de vraag voluit"); return; }
                  doe(async () => {
                    const { error } = await supabase.from("geo_prompts").upsert([{ site_id: data.site.id, vraag: v.slice(0, 300), dienst_id: r.d.id, taal: "nl" }], { onConflict: "site_id,vraag" });
                    if (error) throw new Error(error.message);
                    setNieuweVraag({ ...nieuweVraag, [r.d.id]: "" });
                  }, "Vraag toegevoegd, de planning meet hem vanzelf");
                }}>Voeg toe</button>
              </div>
            </Rij>
          );
        })}
      </Groep>
      <p style={{ fontSize: 11, color: C.dim, marginTop: 10 }}>Zoekdata uit Search Console, backlinks, omzet per zoekwoord en de effectmeting volgen in de volgende fasen (zie het ontwerp). De technische controle en klantvragen draaien via het tabblad hierboven en de planning.</p>
    </div>
  );
}
